/**
 * DataAdjustmentsPage - Refactored Version
 * 
 * Main orchestration component for the Data Adjustments workflow.
 * Uses custom hooks for feature logic and section components for UI.
 * 
 * Workflow Steps:
 * 1. File Upload (multiple CSV files)
 * 2. Parameter Input (start time, end time, time step, offset)
 * 3. Method Selection (if needed for time adjustment)
 * 4. Data Processing
 * 5. Export Results
 * 
 * Reduced from ~1,724 lines to ~450 lines through modularization.
 */

import React, { useReducer, useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Core services
import { useKeepAlive } from '@/core/services/keepalive';
import { useQuotaCheck } from '@/features/subscription/hooks/useQuotaCheck';
import { useAuth } from '@/features/auth/contexts/AuthContext';

// Feature hooks
import { useAdjustmentsFile } from '@/features/data-adjustments/hooks/useAdjustmentsFile';
import { useAdjustmentsParameters } from '@/features/data-adjustments/hooks/useAdjustmentsParameters';
import { useAdjustmentsUpload } from '@/features/data-adjustments/hooks/useAdjustmentsUpload';
import { useAdjustmentsSave } from '@/features/data-adjustments/hooks/useAdjustmentsSave';

// Feature components
import FileUploadSection from '@/features/data-adjustments/components/FileUploadSection';
import ParametersSection from '@/features/data-adjustments/components/ParametersSection';
import MethodSelectionSection from '@/features/data-adjustments/components/MethodSelectionSection';
import OriginalDataSection from '@/features/data-adjustments/components/OriginalDataSection';
import ProcessedDataSection from '@/features/data-adjustments/components/ProcessedDataSection';
import ChartsSection from '@/features/data-adjustments/components/ChartsSection';
import ExportSection from '@/features/data-adjustments/components/ExportSection';

// Feature types and reducer
import {
  WORKFLOW_STEPS,
  transformToAnalysisFormat,
  type DateTimeState,
  type BrushRange,
} from '@/features/data-adjustments/types/dataAdjustments.types';
import { deriveOffsetFromAnchor } from '@/features/first-processing/utils/deriveOffset';
import { uiReducer, initialUIState } from '@/features/data-adjustments/reducers/uiReducer';
import AdjustmentsOfDataChunkedUploader from '@/features/data-adjustments/services/AdjustmentsOfDataChunkedUploader';
import { mapAdjustmentsBackendError } from '@/features/data-adjustments/utils/adjustmentsBackendErrorMapper';

// Shared components
import WorkflowStepper from '@/shared/components/layout/WorkflowStepper';
import NetworkStatusIndicator from '@/shared/components/feedback/NetworkStatusIndicator';
import SuccessMessage from '@/shared/components/feedback/SuccessMessage';
import ErrorMessage from '@/shared/components/feedback/ErrorMessage';
import ProgressTracker, { type UploadProgress as StandardUploadProgress } from '@/shared/components/feedback/ProgressTracker';
import FileNameModal from '@/shared/components/modals/FileNameModal';
import SubmitButton from '@/shared/components/buttons/SubmitButton';
import RestartButton from '@/shared/components/buttons/RestartButton';
import { UpgradeModal } from '@/features/subscription/components';

// Types
import type { AdjustmentsProcessingParams, DecimalPrecision } from '@/types/common';

// Shared hooks
import { useWorkflowSteps } from '@/shared/hooks/useWorkflowSteps';
import { usePublishChatContext } from '@/features/chatbot/usePublishChatContext';
import type { ChatField } from '@/features/chatbot/types';

const DataAdjustmentsPage: React.FC = () => {
  const { t } = useTranslation('dataAdjustments');

  // ═══════════════════════════════════════════════════════════════════
  // UI State Management (via reducer)
  // ═══════════════════════════════════════════════════════════════════
  const [uiState, dispatch] = useReducer(uiReducer, initialUIState);

  // Workflow step management
  const { isMountedRef, markStepCompleted, moveToStep } = useWorkflowSteps(dispatch);

  // Quota checking
  const { canProcess, refreshUsage } = useQuotaCheck();
  const { currentUser } = useAuth();

  // ═══════════════════════════════════════════════════════════════════
  // Local UI State
  // ═══════════════════════════════════════════════════════════════════
  
  const [isLoading, setIsLoading] = useState(false);
  
  // Upgrade modal
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [upgradeResourceType, setUpgradeResourceType] = useState<'upload' | 'processing' | 'storage'>('processing');

  // Chart brush sync
  const [sharedBrushRange, setSharedBrushRange] = useState<BrushRange | null>(null);

  // Keep connection alive during operations
  useKeepAlive(isLoading);

  // ═══════════════════════════════════════════════════════════════════
  // Upgrade Modal Handler
  // ═══════════════════════════════════════════════════════════════════
  const handleShowUpgradeModal = useCallback((message: string, type: 'upload' | 'processing' | 'storage') => {
    setUpgradeMessage(message);
    setUpgradeResourceType(type);
    setShowUpgradeModal(true);
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // Feature Hooks
  // ═══════════════════════════════════════════════════════════════════

  // Parameters hook
  const parametersHook = useAdjustmentsParameters();

  // Publish current adjustment settings to the chatbot assistant context
  const chatContext = useMemo(() => {
    const fields: ChatField[] = [];
    if (parametersHook.startTime?.value) fields.push({ label: 'Startzeit (UTC)', value: parametersHook.startTime.value });
    if (parametersHook.endTime?.value) fields.push({ label: 'Endzeit (UTC)', value: parametersHook.endTime.value });
    if (parametersHook.timeStepSize) fields.push({ label: 'Zeitschrittweite [min]', value: parametersHook.timeStepSize });
    if (parametersHook.offset) fields.push({ label: 'Offset [min]', value: parametersHook.offset });
    return { fields };
  }, [parametersHook.startTime, parametersHook.endTime, parametersHook.timeStepSize, parametersHook.offset]);
  usePublishChatContext(chatContext);

  // File upload hook
  const fileHook = useAdjustmentsFile({
    onStartTimeChange: (state: DateTimeState) => {
      parametersHook.setStartTime(state.value, state.timestamp);
    },
    onEndTimeChange: (state: DateTimeState) => {
      parametersHook.setEndTime(state.value, state.timestamp);
    },
    onShowUpgradeModal: handleShowUpgradeModal,
  });

  // Upload/processing hook
  const uploadHook = useAdjustmentsUpload();

  // Save/export hook
  const saveHook = useAdjustmentsSave({
    adjustedData: uploadHook.adjustedData,
    processedDataFrames: uploadHook.processedDataFrames,
    startTime: parametersHook.startTime,
    endTime: parametersHook.endTime,
    timeStepSize: parametersHook.timeStepSize,
    offset: parametersHook.offset,
    selectedMethods: parametersHook.selectedMethods,
    intrplMaxValues: parametersHook.intrplMaxValues,
    dispatch,
  });

  // ═══════════════════════════════════════════════════════════════════
  // Workflow Step Auto-progression
  // ═══════════════════════════════════════════════════════════════════

  // Step 1 → 2: After successful file upload
  useEffect(() => {
    if (!isMountedRef.current) return;
    if (fileHook.processedData?.success && uiState.currentStep === 1) {
      markStepCompleted(1);
      moveToStep(2);
    }
  }, [fileHook.processedData, uiState.currentStep, markStepCompleted, moveToStep, isMountedRef]);

  // Step 2 → 3: After parameters are filled
  useEffect(() => {
    if (!isMountedRef.current) return;
    const offsetOrAnchorFilled = parametersHook.isConst
      ? parametersHook.offset !== ''
      : parametersHook.anchorTime !== '';
    const hasAllParams =
      parametersHook.startTime.value !== '' &&
      parametersHook.endTime.value !== '' &&
      parametersHook.timeStepSize !== '' &&
      offsetOrAnchorFilled &&
      fileHook.processedData;

    if (hasAllParams && uiState.currentStep === 2) {
      markStepCompleted(2);
      moveToStep(3);
    }
  }, [
    parametersHook.startTime.value,
    parametersHook.endTime.value,
    parametersHook.timeStepSize,
    parametersHook.offset,
    parametersHook.anchorTime,
    parametersHook.isConst,
    fileHook.processedData,
    uiState.currentStep,
    markStepCompleted,
    moveToStep,
    isMountedRef,
  ]);

  // Step 3: Method selection completion
  useEffect(() => {
    if (!isMountedRef.current) return;
    if (parametersHook.methodsRequired && parametersHook.filesNeedingMethods.length > 0) {
      if (parametersHook.allMethodsSelected()) {
        markStepCompleted(3);
      }
    } else if (!parametersHook.methodsRequired && uiState.currentStep >= 3 && fileHook.processedData) {
      markStepCompleted(3);
    }
  }, [
    parametersHook.methodsRequired,
    parametersHook.filesNeedingMethods,
    parametersHook.allMethodsSelected,
    uiState.currentStep,
    fileHook.processedData,
    markStepCompleted,
    isMountedRef,
  ]);

  // ═══════════════════════════════════════════════════════════════════
  // Consolidated Error/Success Handling
  // ═══════════════════════════════════════════════════════════════════

  // Mirror hook errors into reducer. Always dispatch the latest hook value
  // (even null), so stale toasts get cleared when the underlying hook
  // clears its own error. Priority: most-recent fire wins.
  useEffect(() => {
    if (fileHook.error !== undefined) {
      dispatch({ type: 'SET_ERROR', payload: fileHook.error });
    }
  }, [fileHook.error]);

  useEffect(() => {
    if (uploadHook.uploadError !== undefined) {
      dispatch({ type: 'SET_ERROR', payload: uploadHook.uploadError });
    }
  }, [uploadHook.uploadError]);

  useEffect(() => {
    if (saveHook.error !== undefined) {
      dispatch({ type: 'SET_ERROR', payload: saveHook.error });
    }
  }, [saveHook.error]);

  // Mirror hook success messages into reducer
  useEffect(() => {
    if (fileHook.successMessage !== undefined) {
      dispatch({ type: 'SET_SUCCESS', payload: fileHook.successMessage });
    }
  }, [fileHook.successMessage]);

  useEffect(() => {
    if (saveHook.successMessage !== undefined) {
      dispatch({ type: 'SET_SUCCESS', payload: saveHook.successMessage });
    }
  }, [saveHook.successMessage]);

  // Sync loading state
  useEffect(() => {
    setIsLoading(fileHook.isUploading || uploadHook.isProcessing || saveHook.isLoading);
  }, [fileHook.isUploading, uploadHook.isProcessing, saveHook.isLoading]);

  // ═══════════════════════════════════════════════════════════════════
  // Handlers
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Process data with parameters
   */
  const handleProcess = useCallback(async () => {
    // Check processing quota
    const quotaCheck = await canProcess();
    if (!quotaCheck.allowed) {
      handleShowUpgradeModal(
        t('errors.processingLimitReached'),
        'processing'
      );
      return;
    }

    setIsLoading(true);
    moveToStep(4);

    try {
      // Get filenames
      const fileNames = fileHook.processedData?.data?.info_df?.map(
        (info) => info['Name der Datei'].replace('_processed', '')
      ) || [];

      if (fileNames.length === 0) {
        dispatch({ type: 'SET_ERROR', payload: t('errors.noFileSelected') });
        setIsLoading(false);
        return;
      }

      // Derive effective offset: when !isConst, compute from anchor time
      let effectiveOffsetValue = parametersHook.offset ? parseFloat(parametersHook.offset) : 0;
      if (!parametersHook.isConst && parametersHook.anchorTime) {
        try {
          const firstFileStartTime = fileHook.processedData?.data?.info_df?.[0]?.['Startzeit (UTC)'];
          if (firstFileStartTime) {
            effectiveOffsetValue = deriveOffsetFromAnchor(
              parametersHook.anchorTime,
              firstFileStartTime,
              parseFloat(parametersHook.timeStepSize)
            );
          }
        } catch {
          effectiveOffsetValue = 0;
        }
      }

      // Prepare processing parameters
      const processingParams: AdjustmentsProcessingParams = {
        startTime: parametersHook.startTime.value || null,
        endTime: parametersHook.endTime.value || null,
        timeStepSize: parametersHook.timeStepSize ? parseFloat(parametersHook.timeStepSize) : 0,
        offset: effectiveOffsetValue,
        methods: Object.fromEntries(
          fileNames.map((fileName) => [
            fileName,
            {
              method: parametersHook.selectedMethods[fileName] || '',
              intrpl_max:
                parametersHook.selectedMethods[fileName] === 'intrpl' ||
                parametersHook.selectedMethods[fileName] === 'nearest (max. delta)'
                  ? Number(parametersHook.intrplMaxValues[fileName])
                  : undefined,
              decimal_precision: (parametersHook.decimalPlaces[fileName] || 'full') as DecimalPrecision,
            },
          ])
        ),
      };

      // Process data
      const methodsRequired = await uploadHook.processData(
        processingParams,
        fileHook.uploadIds,
        fileNames
      );

      if (methodsRequired) {
        // Methods are required - set up method selection
        const requestedStep = parseFloat(parametersHook.timeStepSize);
        const requestedOffset = parametersHook.offset ? parseFloat(parametersHook.offset) : 0;

        const originalTimesteps = fileHook.processedData?.data?.info_df.map((info) => ({
          filename: info['Name der Datei'],
          current_timestep: info['Zeitschrittweite [min]'],
          current_offset: info['Offset [min]'] || 0,
        })) || [];

        const filesNeedingAdjustment = originalTimesteps.filter(
          (file) =>
            file.current_timestep !== requestedStep ||
            file.current_offset !== requestedOffset
        );

        if (filesNeedingAdjustment.length > 0) {
          parametersHook.setFilesNeedingMethods(
            filesNeedingAdjustment.map((file) => ({
              filename: file.filename,
              current_timestep: file.current_timestep,
              requested_timestep: requestedStep,
              current_offset: file.current_offset,
              requested_offset: requestedOffset,
            }))
          );
          parametersHook.setMethodsRequired(true);
          dispatch({ type: 'SET_SUCCESS', payload: t('success.selectMethod') });
        }
      } else {
        // Processing complete
        if (currentUser) {
          await refreshUsage();
        }
        dispatch({ type: 'SET_SUCCESS', payload: t('success.allProcessed') });
        markStepCompleted(4);
        moveToStep(5);
      }
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: err instanceof Error ? err.message : t('errors.unknownError', { defaultValue: 'An unknown error occurred' }) });
    } finally {
      setIsLoading(false);
    }
  }, [
    canProcess,
    handleShowUpgradeModal,
    t,
    moveToStep,
    fileHook.processedData,
    fileHook.uploadIds,
    parametersHook,
    uploadHook,
    currentUser,
    refreshUsage,
    markStepCompleted,
  ]);

  /**
   * Restart workflow
   */
  const handleRestart = useCallback(() => {
    // Disconnect socket connections
    const uploadIdsList = Object.values(fileHook.uploadIds);
    if (uploadIdsList.length > 0) {
      uploadIdsList.forEach((uploadId) => {
        AdjustmentsOfDataChunkedUploader.disconnectSocket(uploadId);
      });
    } else {
      AdjustmentsOfDataChunkedUploader.disconnectSocket();
    }

    // Reset all hooks
    fileHook.resetFiles();
    parametersHook.resetParameters();
    uploadHook.resetUpload();
    saveHook.resetSave();

    // Reset non-reducer local state
    setIsLoading(false);
    setSharedBrushRange(null);

    // Reset workflow (clears errorMessage + successMessage + errorPersistent)
    dispatch({ type: 'RESET_UI' });
    dispatch({ type: 'SET_STEP', payload: 1 });

    // Show "data reset" success toast AFTER RESET_UI so it isn't erased
    dispatch({ type: 'SET_SUCCESS', payload: t('success.dataReset') });

    // Prevent auto-progression
    isMountedRef.current = false;
    setTimeout(() => {
      isMountedRef.current = true;
    }, 100);
  }, [fileHook, parametersHook, uploadHook, saveHook, t, dispatch, isMountedRef]);

  // ═══════════════════════════════════════════════════════════════════
  // Derived State
  // ═══════════════════════════════════════════════════════════════════

  // Memoized original table data
  const originalTableData = useMemo(
    () => fileHook.processedData?.data?.info_df?.map(transformToAnalysisFormat) ?? [],
    [fileHook.processedData?.data?.info_df]
  );

  // Current progress - normalize to UploadProgress format
  const currentProgress = useMemo((): StandardUploadProgress | null => {
    if (fileHook.standardProgress) {
      return fileHook.standardProgress;
    }
    if (uploadHook.uploadProgress) {
      // Normalize AdjustmentsUploadProgress to StandardUploadProgress
      const progress = uploadHook.uploadProgress;
      return {
        fileName: progress.message || 'Processing...',
        uploadProgress: progress.uploadProgress || progress.progress || 0,
        processingProgress: progress.processingProgress || 0,
        status: progress.status,
        error: progress.error,
        message: progress.message,
        step: progress.step,
        eta: progress.eta,
        etaFormatted: progress.etaFormatted,
        currentFile: progress.currentFile,
        totalFiles: progress.totalFiles,
      };
    }
    return null;
  }, [fileHook.standardProgress, uploadHook.uploadProgress]);

  // Has data for export
  const hasProcessedData = uploadHook.processedDataFrames.length > 0;

  // ═══════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════

  return (
    <>
      {/* Messages Container */}
      <div className="messages-container" role="region" aria-label="Status messages" aria-live="polite">
        <div className="messages-wrapper">
          {uiState.errorMessage && (() => {
            const mapped = mapAdjustmentsBackendError({
              code: uiState.errorCode ?? undefined,
              message: uiState.errorMessage,
              suggestion: uiState.errorSuggestion ?? undefined,
            });
            const displayMessage = mapped.translationKey
              ? t(mapped.translationKey, { defaultValue: mapped.fallbackMessage })
              : mapped.fallbackMessage;
            return (
              <ErrorMessage
                message={displayMessage}
                onClose={() => dispatch({ type: 'SET_ERROR', payload: null })}
                duration={uiState.errorPersistent ? 0 : 5000}
              />
            );
          })()}
          <SuccessMessage
            message={uiState.successMessage}
            onClose={() => dispatch({ type: 'SET_SUCCESS', payload: null })}
            duration={5000}
          />
        </div>
      </div>

      <NetworkStatusIndicator />

      <div className="main-container relative" role="main" aria-label="Data Adjustments">
        <nav aria-label="Workflow progress">
          <WorkflowStepper
            steps={WORKFLOW_STEPS}
            currentStep={uiState.currentStep}
            completedSteps={uiState.completedSteps}
            namespace="dataAdjustments"
          />
        </nav>

        <div className="bg-card rounded-lg shadow-lg p-6 relative" role="form" aria-label="Data adjustments form">
          {/* Progress Overlay */}
          {isLoading && currentProgress && (
            <div 
              className="fixed inset-0 bg-background/90 z-50 flex items-center justify-center p-4"
              role="dialog"
              aria-labelledby="processing-title"
              aria-modal="true"
            >
              <ProgressTracker
                progress={currentProgress}
                className="my-4 sm:my-8"
              />
            </div>
          )}

          {/* Step 1: File Upload */}
          <FileUploadSection
            selectedFiles={fileHook.selectedFiles}
            onFileSelect={fileHook.handleFileSelect}
            onRemoveFile={fileHook.handleRemoveFile}
            onClearAll={fileHook.handleClearAllFiles}
            onUpload={fileHook.handleUpload}
            isUploading={fileHook.isUploading}
            fileInputRef={fileHook.fileInputRef}
          />

          {/* Original Data Table */}
          {originalTableData.length > 0 && (
            <OriginalDataSection processedData={fileHook.processedData} />
          )}

          {/* Step 2: Parameters */}
          {originalTableData.length > 0 && (
            <ParametersSection
              startTime={parametersHook.startTime}
              endTime={parametersHook.endTime}
              timeStepSize={parametersHook.timeStepSize}
              offset={parametersHook.offset}
              isConst={parametersHook.isConst}
              anchorTime={parametersHook.anchorTime}
              validationErrors={parametersHook.validationErrors}
              onStartTimeChange={parametersHook.setStartTime}
              onEndTimeChange={parametersHook.setEndTime}
              onTimeStepSizeChange={parametersHook.setTimeStepSize}
              onOffsetChange={parametersHook.setOffset}
              onAnchorTimeChange={parametersHook.setAnchorTime}
              onProcess={handleProcess}
              onRestart={handleRestart}
              isLoading={isLoading}
              showSection={true}
              hideButtons={true}
            />
          )}

          {/* Processing and restart buttons — initial config only (no method selection, no results) */}
          {originalTableData.length > 0 && !parametersHook.methodsRequired && !hasProcessedData && (
            <div className="mt-4 sm:mt-8 flex justify-start items-center gap-2 sm:gap-4 flex-wrap mb-4 sm:mb-8">
              <SubmitButton
                onClick={handleProcess}
                disabled={
                  isLoading ||
                  !parametersHook.timeStepSize ||
                  parseFloat(parametersHook.timeStepSize) < 1 ||
                  parseFloat(parametersHook.timeStepSize) > 1440 ||
                  (parametersHook.isConst ? parametersHook.offset === '' : parametersHook.anchorTime === '')
                }
              >
                {isLoading ? t('processing', { ns: 'dataProcessing' }) : t('buttons.processData')}
              </SubmitButton>
              <RestartButton
                onRestart={handleRestart}
                iconSize={28}
                disabled={isLoading}
                aria-label="Reset all data"
              />
            </div>
          )}

          {/* Step 3: Method Selection */}
          {parametersHook.methodsRequired && (
            <MethodSelectionSection
              filesNeedingMethods={parametersHook.filesNeedingMethods}
              selectedMethods={parametersHook.selectedMethods}
              intrplMaxValues={parametersHook.intrplMaxValues}
              decimalPlaces={parametersHook.decimalPlaces}
              onMethodChange={parametersHook.setSelectedMethod}
              onIntrplMaxChange={parametersHook.setIntrplMaxValue}
              onDecimalPlacesChange={parametersHook.setDecimalPlaces}
              onSubmit={handleProcess}
              allMethodsSelected={parametersHook.allMethodsSelected()}
              isLoading={isLoading}
            />
          )}

          {/* Step 4: Processed Data Results */}
          {hasProcessedData && (
            <>
              <ProcessedDataSection
                adjustedData={uploadHook.adjustedData}
                processedDataFrames={uploadHook.processedDataFrames}
              />
              <ChartsSection
                processedDataFrames={uploadHook.processedDataFrames}
                adjustedData={uploadHook.adjustedData}
                sharedBrushRange={sharedBrushRange}
                onSharedBrushChange={setSharedBrushRange}
              />
            </>
          )}

          {/* Step 5: Export */}
          {hasProcessedData && (
            <ExportSection
              onSave={saveHook.openSaveModal}
              onRestart={handleRestart}
              hasData={hasProcessedData}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>

      {/* File Name Modal */}
      <FileNameModal
        isOpen={saveHook.isNameModalOpen}
        originalFileName={saveHook.originalFileName}
        saveDataName={saveHook.saveDataName}
        setSaveDataName={saveHook.setSaveDataName}
        onClose={() => saveHook.setIsNameModalOpen(false)}
        onSave={saveHook.saveData}
        setError={(msg: string) => dispatch({ type: 'SET_ERROR', payload: msg })}
        isLoading={saveHook.isLoading}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        resourceType={upgradeResourceType}
        message={upgradeMessage}
      />
    </>
  );
};

export default DataAdjustmentsPage;
